import React, { ChangeEvent, FocusEvent, ClipboardEvent } from "react";
import { Label } from "../Label";
import { Field } from "../Field";
import { Error } from "../Error";
import { Button } from "../Button";
import { sanitiseString, concatStringFromClassesArrayList } from "../../Utils";
import { IconTypes } from "../../Icons";

import type { ClassItem } from "../../Types/ClassItem";
import type { IconPosition } from "../../Types/IconPosition";
import type { InputMode } from "../../Types/TextInput/InputMode";

export interface Props {
  name?: string;
  label?: React.ComponentProps<typeof Label>;
  id: string;
  password?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>, ...args: unknown[]) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>, ...args: unknown[]) => void;
  onPaste?: (e: ClipboardEvent<HTMLInputElement>) => void;
  accessiblePlaceholder?: boolean;
  fit?: boolean;
  autoFocus?: boolean;
  value?: string;
  autoComplete?: string;
  zIndex?: number;
  classes?: string;
  fieldId?: string;
  fieldClasses?: string;
  dontWrap?: boolean;
  error?: React.ComponentProps<typeof Error>;
  dataTestId: string;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  type?: string;
  width?: string;
  searchButton?: SearchButton;
  inputMode?: InputMode;
}

export interface SearchButton {
  text: string;
  type?: "button" | "reset" | "submit";
  id?: string;
  dataTestId?: string;
  classes?: string;
  iconType?: IconTypes;
  iconPosition?: IconPosition;
  variants?:
    | Array<
        | "primary"
        | "secondary"
        | "small"
        | "ghost"
        | "disabled"
        | "loader"
        | "filter"
        | "tertiary"
        | "navigation"
      >
    | "primary"
    | "secondary"
    | "small"
    | "ghost"
    | "disabled"
    | "loader"
    | "filter"
    | "tertiary"
    | "navigation";
  onClick?: (...args: unknown[]) => void;
}

const InputElement = (props: Props) => {
  const determineInputType = (): {
    type: string;
    pattern?: string;
    inputMode?: InputMode;
  } => {
    if (props.type === "number") {
      // Type must be "text" or Firefox and Safari will set a blank value to the server if non-numeric characters are entered.
      // They don’t block non-numeric characters: https://bugzilla.mozilla.org/show_bug.cgi?id=1398528
      return {
        type: "text",
        pattern: "[0-9]*",
        inputMode: "numeric",
      };
    } else if (props.type) {
      return {
        type: props.type,
      };
    } else {
      return {
        type: "text",
      };
    }
  };

  const { type, pattern, inputMode } = determineInputType();

  const textInputClassItem: ClassItem = {
    defaultClass: "ons-input ons-input--text ons-input-type__input",
    optionalClasses: [
      props.classes || "",
      props.width !== undefined ? `ons-input-number--w-${props.width}` : "",
      props.accessiblePlaceholder ? "ons-input--placeholder" : "",
      props.error ? "ons-input--error" : "",
    ],
  };
  const textInputClasses: string =
    concatStringFromClassesArrayList(textInputClassItem);

  return (
    <input
      value={props.value}
      style={{
        width: props.fit ? "unset" : "",
        zIndex: props.zIndex || 0,
      }}
      autoFocus={props.autoFocus}
      autoComplete={props.autoComplete}
      type={props.password ? "password" : type}
      pattern={pattern}
      inputMode={inputMode}
      id={props.id}
      className={textInputClasses}
      placeholder={
        props.accessiblePlaceholder && props.label?.text
          ? props.label.text
          : undefined
      }
      onChange={props.onChange}
      onBlur={props.onBlur}
      onPaste={props.onPaste}
      data-testid={props.dataTestId}
      name={props.name}
      min={props.min}
      max={props.max}
      minLength={props.minLength}
      maxLength={props.maxLength}
      aria-describedby={
        props.label && props.label.text && props.label.description
          ? `${props.id}-label-description-hint`
          : undefined
      }
    />
  );
};

export const TextInput = (props: Props) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (props.onChange !== undefined) {
      props.onChange(e, e.target.value);
    }
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>): void => {
    if (props.onBlur !== undefined) {
      props.onBlur(e, e.target.value);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>): void => {
    if (props.onPaste !== undefined) {
      props.onPaste(e);
    }
  };

  const sanitisedId: string = sanitiseString(props.id);
  const sanitisedDataTestId: string = sanitiseString(props.dataTestId);

  const searchButtonClassItem: ClassItem = {
    defaultClass: "ons-search__btn",
    optionalClasses: [props.searchButton?.classes || ""],
  };
  const searchButtonClasses: string = concatStringFromClassesArrayList(
    searchButtonClassItem
  );

  const searchButtonIconPosition: IconPosition =
    props.searchButton?.iconPosition || "only";
  let useDefaultIcon: boolean = false;

  if (
    props.searchButton &&
    !props.searchButton.iconType &&
    searchButtonIconPosition === "only"
  ) {
    useDefaultIcon = true;
  }

  const renderTextInput = () => {
    return (
      <>
        {props.label && props.label.text && (
          <Label
            for={sanitisedId}
            id={props.label.id}
            text={props.label.text}
            classes={props.label.classes}
            description={props.label.description}
            attributes={props.label.attributes}
            accessiblePlaceholder={props.accessiblePlaceholder}
            dataTestId={sanitisedDataTestId}
          />
        )}
        {props.searchButton ? (
          <span
            className={
              "ons-grid-flex ons-grid-flex--vertical-top ons-input_search-button"
            }
          >
            <InputElement
              value={props.value}
              autoFocus={props.autoFocus}
              autoComplete={props.autoComplete}
              type={props.password ? "password" : props.type}
              accessiblePlaceholder={props.accessiblePlaceholder}
              password={props.password}
              id={sanitisedId}
              classes={props.classes}
              label={props.label}
              onChange={handleChange}
              onBlur={handleBlur}
              onPaste={handlePaste}
              dataTestId={sanitisedDataTestId}
              name={props.name}
              min={props.min}
              max={props.max}
              minLength={props.minLength}
              maxLength={props.maxLength}
              width={props.width}
              fit={props.fit}
              error={props.error}
              zIndex={props.zIndex}
            />
            {props.searchButton && (
              <Button
                text={props.searchButton.text}
                type={props.searchButton?.type}
                id={
                  sanitiseString(props.searchButton?.id) || `${props.id}-button`
                }
                dataTestId={
                  sanitiseString(props.searchButton?.dataTestId) ||
                  `${props.dataTestId}-button`
                }
                classes={searchButtonClasses}
                iconType={
                  useDefaultIcon ? "Search" : props.searchButton?.iconType // Sets the button's icon to "Search" when iconPosition is "only" and iconType is not provided in searchButton prop
                }
                iconPosition={searchButtonIconPosition}
                onClick={props.searchButton?.onClick}
                variants={props.searchButton?.variants || "small"}
              />
            )}
          </span>
        ) : (
          <InputElement
            value={props.value}
            autoFocus={props.autoFocus}
            autoComplete={props.autoComplete}
            type={props.password ? "password" : props.type}
            accessiblePlaceholder={props.accessiblePlaceholder}
            password={props.password}
            id={sanitisedId}
            classes={props.classes}
            label={props.label}
            onChange={handleChange}
            onBlur={handleBlur}
            onPaste={handlePaste}
            dataTestId={sanitisedDataTestId}
            name={props.name}
            min={props.min}
            max={props.max}
            minLength={props.minLength}
            maxLength={props.maxLength}
            width={props.width}
            fit={props.fit}
            error={props.error}
            zIndex={props.zIndex}
          />
        )}
      </>
    );
  };

  return (
    <Field
      id={props.fieldId}
      classes={props.fieldClasses}
      dontWrap={props.dontWrap}
      dataTestId={`field-${sanitisedDataTestId}`}
      inline={props.label?.inline}
      error={props.error}
    >
      {renderTextInput()}
    </Field>
  );
};
